import { brand } from "@/config/brand";

/**
 * Single-subdomain portal routing seam.
 *
 * The client portal is served two ways from the same bundle:
 * - On its own host (`brand.portalDomain`, e.g. `portal.mydomain.com`) the firm
 *   handle sits at the root: `portal.mydomain.com/<handle>`. Routes have no
 *   prefix and the landing is the index route.
 * - Anywhere else (the staff app host, and single-host local dev) the portal
 *   lives under `/portal/...` so staff + portal coexist in one router.
 *
 * `portalHref()` is the one place that knows which mode we're in, so every
 * in-app portal navigation stays correct without each call site checking the
 * host. It builds *client-side router* paths only — backend API paths
 * (`features/portal/api.ts`) always keep their `/portal/...` shape.
 */
export const isPortalHost =
  typeof window !== "undefined" &&
  window.location.hostname === brand.portalDomain;

export const PORTAL_BASE = isPortalHost ? "" : "/portal";

/**
 * Build a portal router path. `portalHref("/home")` → `/home` on the portal
 * host, `/portal/home` elsewhere; `portalHref()` → the portal root.
 */
export function portalHref(sub = ""): string {
  return `${PORTAL_BASE}${sub}` || "/";
}

/**
 * Where to send a taxpayer to sign in: their firm's branded sign-in
 * (`/<handle>`) when we know the handle, otherwise the generic portal root where
 * they type it. Used by every post-auth redirect (logout, guard, switch-client).
 */
export function portalSignInHref(handle?: string | null): string {
  return handle ? portalHref(`/${handle}`) : portalHref();
}

/**
 * Handles a firm may not take — they'd shadow a portal-host static route or are
 * otherwise unsafe. Mirrors the backend `PORTAL_NAME_RESERVED` (the enforcement
 * boundary); this copy is for client-side UX only.
 */
export const PORTAL_RESERVED_NAMES = new Set([
  // Fixed portal API segments that resolve before the handle catch-all.
  "clients",
  "logout",
  "me",
  "onboarding",
  "documents",
  "worksheet",
  "staff",
  // Portal-host SPA static routes.
  "home",
  "profile",
  "notices",
  "proposals",
  "financials",
  "password",
  "switch-client",
  "requested-documents",
  "cookies",
  "auth",
  // Generic / auth words.
  "login",
  "signup",
  "forgot",
  "api",
  "admin",
  "portal",
  "www",
]);

export const isReservedPortalName = (value: string) =>
  PORTAL_RESERVED_NAMES.has(value.trim().toLowerCase());
